import React from 'react';
import { format } from 'date-fns';
import { useCRM } from '../../context/CRMContext';

const Payslip = ({ record, user }) => {
  const { settings, salaries } = useCRM();

  if (!record || !user) return null;

  // Find salary config to determine currency
  const userSalary = salaries?.find(s => s.userId === user.id);
  const currency = userSalary?.currency || 'USD';

  const currencySymbol = (curr) => {
    switch(curr) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'AED': return 'AED ';
      case 'INR': return '₹';
      default: return '$';
    }
  };
  
  const symbol = currencySymbol(currency);
  const userName = user.fullName || user.full_name || 'Employee';
  
  // Safe parsing
  const baseSalary = parseFloat(record.baseSalary) || 0;
  const totalAllowances = parseFloat(record.totalAllowances) || 0;
  const totalDeductions = parseFloat(record.totalDeductions) || 0;
  const netSalary = parseFloat(record.netSalary) || 0;

  return (
    <div className="max-w-3xl mx-auto bg-white p-8 border border-gray-200 shadow-sm" id="payslip-content">
      {/* Header */}
      <div className="flex justify-between items-start border-b-2 border-gray-800 pb-6 mb-6">
        <div className="flex items-center space-x-4">
          {settings.companyLogo ? (
            <img src={settings.companyLogo} alt="Logo" className="h-16 w-16 object-contain" />
          ) : (
            <div className="h-16 w-16 bg-gray-100 flex items-center justify-center rounded">
              <span className="text-xl font-bold text-gray-400">{settings.companyName.charAt(0)}</span>
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{settings.companyName}</h1>
            <p className="text-sm text-gray-600 whitespace-pre-line">{settings.companyAddress}</p>
            <p className="text-sm text-gray-600">{settings.companyEmail}</p>
          </div>
        </div>
        <div className="text-right">
          <h2 className="text-3xl font-bold text-gray-400 uppercase tracking-widest">Payslip</h2>
          <p className="text-lg font-medium text-gray-900 mt-2">
            {record.paymentMonth ? format(new Date(record.paymentMonth), 'MMMM yyyy') : 'Unknown Month'}
          </p>
          <p className="text-xs text-gray-500">Generated: {format(new Date(), 'dd MMM yyyy')}</p>
        </div>
      </div>

      {/* Employee Info */}
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-xs font-bold text-gray-400 uppercase mb-1">Employee Details</h3>
          <p className="font-bold text-gray-900 text-lg">{userName}</p>
          <p className="text-gray-600">{user.role}</p>
          <p className="text-gray-600 text-sm">{user.email}</p>
        </div>
        <div className="text-right">
          <h3 className="text-xs font-bold text-gray-400 uppercase mb-1">Payment Status</h3>
          <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full font-medium text-sm capitalize">
            {record.status}
          </span>
          <p className="text-gray-600 text-sm mt-2">
            Paid on: {record.paymentDate ? format(new Date(record.paymentDate), 'dd MMM yyyy') : 'Pending'}
          </p>
        </div>
      </div>

      {/* Earnings Table */}
      <div className="mb-8">
        <h3 className="text-sm font-bold text-gray-900 uppercase border-b border-gray-300 pb-2 mb-4">Earnings & Allowances</h3>
        <div className="space-y-2">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Base Salary</span>
            <span className="font-medium text-gray-900">{symbol}{baseSalary.toLocaleString()}</span>
          </div>
          {totalAllowances > 0 && (
             <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Allowances (Housing, Transport, Other)</span>
              <span className="font-medium text-gray-900">{symbol}{totalAllowances.toLocaleString()}</span>
            </div>
          )}
          <div className="flex justify-between py-2 bg-gray-50 font-bold px-2 rounded mt-2">
            <span>Total Earnings</span>
            <span>{symbol}{(baseSalary + totalAllowances).toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Deductions Table */}
      <div className="mb-8">
        <h3 className="text-sm font-bold text-gray-900 uppercase border-b border-gray-300 pb-2 mb-4">Deductions</h3>
        <div className="space-y-2">
          <div className="flex justify-between py-2 border-b border-gray-100">
            <span className="text-gray-600">Standard Deductions</span>
            <span className="font-medium text-red-600">-{symbol}{totalDeductions.toLocaleString()}</span>
          </div>
          <div className="flex justify-between py-2 bg-gray-50 font-bold px-2 rounded mt-2">
            <span>Total Deductions</span>
            <span className="text-red-700">-{symbol}{totalDeductions.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Net Pay */}
      <div className="bg-gray-900 text-white p-6 rounded-lg flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-400 uppercase tracking-wider">Net Payable Amount</p>
          <p className="text-xs text-gray-500 mt-1">*Transfer to bank account</p>
        </div>
        <div className="text-3xl font-bold">
          {symbol}{netSalary.toLocaleString()}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-12 text-center text-xs text-gray-400 border-t border-gray-200 pt-6">
        <p>This is a system generated payslip.</p>
        <p>{settings.companyName} • {settings.companyEmail}</p>
      </div>
    </div>
  );
};

export default Payslip;